import {z} from "zod";

import {EnterpriseHandle, OrganizationPrivateHandle, OrganizationPublicHandle, UserHandle} from "./handle";
import {AccountUid, isEnterpriseUid, isOrganizationUid} from "./identifier";
import {IBaseMixin, ITimestampMixin} from "./mixin";
import {IUserData, IUserPrivateMetadata} from "./user";
import {
    Enterprise,
    EnterpriseWithDefaultOrganization,
    NonEnterpriseOrganization,
    Organization,
    OrganizationPrivateMetadata,
    OrganizationWithEnterpriseWithDefaultOrganization,
} from "./workspace";

export const AccountType = z.enum(["user", "organization", "enterprise"]);
export type AccountType = z.infer<typeof AccountType>;

export type Account = IUserData | Organization;
export type AccountPublicHandle = UserHandle | OrganizationPublicHandle;
export type AccountPrivateMetadata = IUserPrivateMetadata | OrganizationPrivateMetadata;

export type AccountWithDetails =
    | IUserData
    | NonEnterpriseOrganization
    | OrganizationWithEnterpriseWithDefaultOrganization
    | EnterpriseWithDefaultOrganization;

/**
 * The combination of both an account, which may be the current user or an organization/enterprise, and the current user
 *
 * Useful for places where we always need to know the current user, but that user can also be acting within an
 * organization
 */
export type AccountContext = {
    account: Account | Enterprise;
    currentUser: IUserData;
};

/**
 * An account-scoped context for operations that act on the account itself (e.g. credit
 * recalculation) and therefore must not depend on which user triggered them.
 *
 * `currentUser` is accepted for call-site compatibility with {@link AccountContext} but is
 * optional and never read — org/enterprise entitlements resolve from the account's own metadata.
 */
export type AccountOnlyContext = {
    account: Account | Enterprise;
    currentUser?: IUserData;
};

// eslint-disable-next-line no-restricted-syntax
export function accountIsEnterprise(account: Pick<Account | Enterprise, "uid">): account is Enterprise {
    return isEnterpriseUid(account.uid);
}

// eslint-disable-next-line no-restricted-syntax
export function accountIsOrganization(account: Pick<Account | Enterprise, "uid">): account is Organization {
    return isOrganizationUid(account.uid);
}

// eslint-disable-next-line no-restricted-syntax
export function accountIsUser(account: Pick<Account | Enterprise, "uid">): account is IUserData {
    return !isOrganizationUid(account.uid) && !isEnterpriseUid(account.uid);
}

export interface IAccountFollowData extends IBaseMixin, ITimestampMixin {
    readonly followed_uid: AccountUid;
    readonly followed_account_type: "user" | "organization";
    readonly follower_uid: AccountUid;
    readonly follower_uid_account_type: "user" | "organization";
}

// eslint-disable-next-line no-restricted-syntax
export function createFollowerDocumentUid(followedUid: string, followerUid: string) {
    return `${followedUid}:${followerUid}`;
}

// eslint-disable-next-line no-restricted-syntax
export function createAccountFollowerData(
    followedUid: AccountUid,
    followedAccountType: "user" | "organization",
    followerUid: AccountUid,
    followerAccountType: "user" | "organization",
): IAccountFollowData {
    return {
        uid: createFollowerDocumentUid(followedUid, followerUid),
        created_at: new Date().getTime(),
        updated_at: new Date().getTime(),
        followed_uid: followedUid,
        followed_account_type: followedAccountType,
        follower_uid: followerUid,
        follower_uid_account_type: followerAccountType,
    };
}

interface WithAccountPublicHandle {
    accountHandle: AccountPublicHandle;
    enterpriseHandle?: never;
    enterpriseOrganizationHandle?: never;
}

interface WithEnterpriseHandles {
    accountHandle?: never;
    enterpriseHandle: EnterpriseHandle;
    enterpriseOrganizationHandle?: OrganizationPrivateHandle | undefined;
}

export type WithAccountHandles<T extends object> = T & (WithAccountPublicHandle | WithEnterpriseHandles);

export type SeoBanReason =
    | "unspecified"
    | "spam"
    | "offensive"
    | "harassment"
    | "inappropriate_content"
    | "terms_violation";

export const SeoBanStatus = z.enum([
    "not_reviewed",
    "pending_review",
    "auto_banned",
    "banned",
    "unbanned",
    "auto_not_spam",
]);
export type SeoBanStatus = z.infer<typeof SeoBanStatus>;

export interface ISeoBanData extends IBaseMixin, ITimestampMixin {
    status: SeoBanStatus;
    reason: SeoBanReason;
    moderator: {
        uid: string;
        handle: string;
    };
    uid: string;
    handle: string;
    full_name?: string;
    source?: "manual" | "llm_classifier";
    classification_ref?: string;
}

/**
 * AI usage blocklist entry, keyed by the debited account uid (user or organization).
 *
 * When a doc exists with `blocked: true`, all AI credit spend for the account is refused
 * by the pre-spend credit gate. Used for abuse remediation.
 *
 * Stored at /internal/accounts/aiUsageBlocklist/{accountUid}
 */
export interface IAiUsageBlockData extends IBaseMixin<AccountUid>, ITimestampMixin {
    /** The account's handle, for admin readability */
    handle?: string;
    /** Whether AI credit spend is currently blocked for this account */
    blocked: boolean;
    /** Why the account was blocked */
    reason?: string;
    /** The moderator who last changed the block status */
    moderator?: {
        uid: string;
        handle: string;
    };
}

export interface IVipData extends IBaseMixin, ITimestampMixin {
    created_at: number;
    full_name: string;
    handle: string;
    moderator: {
        handle: string;
        uid: string;
    };
    is_vip: boolean;
    uid: string;
    updated_at: number;
}

/**
 * State values for Firebase Authentication (login) status
 */
export type LifecycleLoginState = "unknown" | "enabled" | "disabled";

/**
 * State values for Stripe subscription status
 * - "unknown": Initial state before any action, or operation failed with unknown result
 * - "no-customer": No Stripe customer exists for this user (nothing to cancel)
 * - "active": Subscription is active and not scheduled to cancel
 * - "cancel-at-period-end-user": User-initiated cancellation (before deletion), should never be undone
 * - "cancel-at-period-end-deletion": Deletion-initiated cancellation, can be undone if within period
 * - "canceled": Final cancellation (period ended), cannot be undone
 * - "mixed": Multiple subscriptions in different states
 *
 * Legacy values (for backward compatibility):
 * - "cancelled": Treated as "unknown source" cancellation, will not be auto-undone on undelete
 * - "not-reactivatable": Alias for "canceled" (final state)
 */
export type LifecycleStripeState =
    | "unknown"
    | "no-customer"
    | "active"
    | "cancel-at-period-end-user"
    | "cancel-at-period-end-deletion"
    | "canceled"
    | "mixed"
    | "cancelled"
    | "not-reactivatable";

/**
 * Per-subscription detail for tracking individual subscription states
 * Used when a user has multiple subscriptions in different states
 */
export interface ILifecycleStripeSubscriptionDetail {
    /** The Stripe subscription ID */
    subscriptionId: string;
    /** The state of this specific subscription */
    state: LifecycleStripeState;
    /** Timestamp when the subscription is scheduled to cancel (if applicable) */
    cancelAt?: number;
    /** Whether this subscription can be restored on undelete */
    canUndo: boolean;
}

/**
 * Extended Stripe sync status with subscription details
 */
export interface ILifecycleStripeSyncStatus extends ILifecycleSyncStatus<LifecycleStripeState> {
    /** Per-subscription details when state is "mixed" or for detailed view */
    subscriptionDetails?: ILifecycleStripeSubscriptionDetail[];
    /** IDs of subscriptions we set to cancel during soft-delete (used for undelete) */
    deletionCanceledSubscriptionIds?: string[];
}

/**
 * State values for user search index (Typesense seo field)
 * These match the values used in the Typesense user search index seo field
 */
export type LifecycleUserSearchState = "unknown" | "removed" | "searchable";

/**
 * State values for profile access (controlled by lifecycle in usersPrivateMetadata)
 */
export type LifecycleProfileAccessState = "unknown" | "enabled" | "disabled";

/**
 * State values for project search index status
 * - "unknown": Initial state before any action, or operation failed
 * - "archived": Projects marked as archived in search index (owner soft-deleted)
 * - "visible": Projects visible in search index (owner active)
 * - "syncing": Sync in progress (messages queued but not yet processed)
 */
export type LifecycleProjectsState = "unknown" | "archived" | "visible" | "syncing";

/**
 * Generic sync status for a single step in the lifecycle change process.
 * The state field contains the actual current state of that system.
 */
export interface ILifecycleSyncStatus<TState extends string = string> {
    /** The current state of this system */
    state: TState;
    /** Timestamp when this state was last updated */
    updatedAt?: number;
    /** Error message if the last operation failed */
    error?: string;
}

/**
 * Lifecycle status data for internal admin tools.
 * This is a minimal view of user lifecycle status, stored in a separate collection
 * from usersPrivateMetadata to allow internal users to subscribe to lifecycle changes
 * without exposing the full private metadata document.
 *
 * The syncStatus object tracks the current state of each downstream system,
 * allowing the admin UI to show real-time status as the backend performs each operation.
 */
export interface ILifecycleData extends IBaseMixin, ITimestampMixin {
    /** The user's uid */
    uid: string;
    /** The user's handle */
    handle: string;
    /** The lifecycle status: "initialized", "deletion-requested", or "deleted" */
    lifecycle: "initialized" | "deletion-requested" | "deleted";
    /** The moderator who last changed the lifecycle status */
    moderator: {
        uid: string;
        handle: string;
    };
    /** Timestamp when the moderator performed the action (preserved across sync updates) */
    moderator_acted_at?: number;
    /** Sync status for each downstream system affected by lifecycle changes */
    syncStatus?: {
        /** Firebase Authentication account status (user-only) */
        login?: ILifecycleSyncStatus<LifecycleLoginState>;
        /** Stripe subscription status (extended with subscription details) */
        stripe?: ILifecycleStripeSyncStatus;
        /** Typesense user search index status (seo field value, user-only) */
        userSearch?: ILifecycleSyncStatus<LifecycleUserSearchState>;
        /** Typesense organization search index status (org-only) */
        orgSearch?: ILifecycleSyncStatus<LifecycleOrgSearchState>;
        /** Profile access status (controlled by lifecycle in usersPrivateMetadata, user-only) */
        profileAccess?: ILifecycleSyncStatus<LifecycleProfileAccessState>;
        /** Project search index status (archived/visible based on owner lifecycle) */
        projects?: ILifecycleSyncStatus<LifecycleProjectsState> & {
            /** Number of projects queued for sync */
            projectCount?: number;
        };
    };
}

/**
 * State values for organization search index (Typesense)
 */
export type LifecycleOrgSearchState = "unknown" | "removed" | "searchable";

/**
 * Educational qualification status data for internal admin tools.
 * This mirrors the educational qualification flag from usersPrivateMetadata.flags.account.educationalQualified
 * into an internal collection that admins can access without exposing full private metadata.
 *
 * Stored at /internal/accounts/educationalQualification/{userUid}
 *
 * This collection has two writers with distinct responsibilities:
 * - Admin function: writes moderator metadata (moderator, source, moderatorActionAt) but NOT educationalQualified
 * - Trigger: writes the mirrored flag (educationalQualified) and sync timestamp (lastSyncedAt)
 */
export interface IEducationalQualificationData extends IBaseMixin, ITimestampMixin {
    /** The user's uid */
    uid: string;
    /** The user's handle */
    handle: string;
    /** Whether the user qualifies for educational benefits (written only by trigger) */
    educationalQualified: boolean;
    /** The moderator who last changed the educational qualification status (if manually changed) */
    moderator?: {
        uid: string;
        handle: string;
    };
    /** Source of the qualification: "auto" for automatic detection, "manual" for admin override */
    source: "auto" | "manual";
    /** Timestamp when a moderator last performed a manual action (written only by admin function) */
    moderatorActionAt?: number;
    /** Timestamp when this record was last synced from usersPrivateMetadata (written only by trigger) */
    lastSyncedAt?: number;
}

/**
 * A "thanks" entry in the internal ThanksBot tool.
 * Stored at /internal/thanks/entries/{autoId}
 */
export interface IThanksEntry extends IBaseMixin, ITimestampMixin {
    /** UID of the sender */
    senderUid: string;
    /** UID of the recipient */
    recipientUid: string;
    /** The reason / message for the thanks */
    reason: string;
}

/**
 * An employee entry in the internal employee directory.
 * Stored at /internal/directory/employees/{autoId}
 */
export interface IEmployeeEntry extends IBaseMixin, ITimestampMixin {
    /** Employee's email address */
    email: string;
    /** First name */
    firstName: string;
    /** Last name */
    lastName: string;
    /** Other aliases (e.g. nicknames, shortened names) */
    aliases: string[];
    /** Display name shown in the UI */
    displayName: string;
    /** Whether the employee is currently active (still at the company) */
    active: boolean;
}
